import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  type ImpersonationState,
  onImpersonationChange,
  readImpersonation,
  setImpersonation,
} from '@/lib/impersonation';
import { trpc } from '@/lib/trpc';
import { useAuth } from '@/lib/auth';
import { toEffectiveRole, type UserRole } from '@shared/types';

// Role → home URL. Kept here so that starting/stopping impersonation can drop
// the admin straight into the target's UI instead of leaving them on whatever
// admin page they happened to be on when they clicked "Preview as".
const ROLE_HOME: Record<string, string> = {
  technician: '/tech',
  manager: '/manager',
  admin: '/admin',
  leadership: '/leadership',
};

interface StartArgs {
  targetUserId: string;
  targetFirstName: string;
  targetLastName: string;
  targetRole: string;
}

export function useImpersonation() {
  const [state, setState] = useState<ImpersonationState | null>(() => readImpersonation());
  const start = trpc.platform.startImpersonation.useMutation();
  const end = trpc.platform.endImpersonation.useMutation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => onImpersonationChange(() => setState(readImpersonation())), []);

  async function startImpersonation(args: StartArgs) {
    try {
      await start.mutateAsync({ targetUserId: args.targetUserId });
      setImpersonation({
        targetUserId: args.targetUserId,
        targetName: `${args.targetFirstName} ${args.targetLastName}`.trim(),
        targetRole: args.targetRole,
      });
      // Flush every cached query so the next render re-fetches as the target.
      queryClient.clear();
      // Drop the admin straight into the target's UI — otherwise they sit on
      // whatever admin page they were viewing and the banner is the only cue.
      const home = ROLE_HOME[args.targetRole] ?? '/';
      navigate(home, { replace: true });
      toast.success(`Previewing as ${args.targetFirstName} ${args.targetLastName}`);
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  async function stopImpersonation() {
    const current = readImpersonation();
    if (!current) return;
    try {
      await end.mutateAsync({ targetUserId: current.targetUserId });
    } catch {
      /* audit write is best-effort */
    }
    setImpersonation(null);
    queryClient.clear();
    // Bounce through "/" so RoleRedirect lands us on the actor's role home.
    navigate('/', { replace: true });
    toast.message('Exited preview.');
  }

  return {
    state,
    isImpersonating: !!state,
    startImpersonation,
    stopImpersonation,
    isStarting: start.isPending,
    isStopping: end.isPending,
  };
}

/**
 * Hook for pages that render a greeting like "Hey Isaac — what are we…".
 * When impersonating, returns the target's first name + role so the page
 * reads like the target sees it. Otherwise falls back to the real user.
 */
export function useEffectiveDisplayIdentity(): {
  firstName: string;
  role: string;
  isImpersonating: boolean;
} {
  const { user } = useAuth();
  const [state, setState] = useState<ImpersonationState | null>(() => readImpersonation());
  useEffect(() => onImpersonationChange(() => setState(readImpersonation())), []);

  if (state) {
    const firstName = state.targetName.split(' ')[0] ?? 'there';
    return { firstName, role: state.targetRole, isImpersonating: true };
  }
  return {
    firstName: user?.firstName ?? 'there',
    role: user?.role ?? 'user',
    isImpersonating: false,
  };
}

/**
 * The user the UI is acting AS: the preview target while impersonating, else
 * the signed-in user.
 *
 * Every "is this mine?" question and every per-user client storage key must use
 * this rather than the session id. Act-as is write-capable (WP7 Phase 6), so a
 * preview that answers those questions as the REAL user gets both halves wrong:
 * "my lines" highlights nothing (the owner owns none of the target's work), and
 * a per-user key stores the target's state under the actor's name, where the
 * next preview of a DIFFERENT person picks it up.
 */
export interface EffectiveUser {
  id: string | null;
  displayName: string | null;
}

export function useEffectiveUser(): EffectiveUser {
  const { user } = useAuth();
  const [state, setState] = useState<ImpersonationState | null>(() => readImpersonation());
  useEffect(() => onImpersonationChange(() => setState(readImpersonation())), []);
  if (state) return { id: state.targetUserId, displayName: state.targetName.trim() || null };
  if (!user) return { id: null, displayName: null };
  return {
    id: user.id,
    displayName: [user.firstName, user.lastName].filter(Boolean).join(' ') || null,
  };
}

/**
 * Returns the role that should drive UI gating.
 *
 *  - While impersonating: the target's role (so "Preview as Dave" swaps
 *    the whole UI to the manager UX, not just the banner).
 *  - Not impersonating, and a platform owner (Isaac, Shane): always
 *    "admin" — Fleetwise operators are master admins and should see
 *    every surface regardless of what their DB role happens to be.
 *  - Everyone else: their own DB role.
 */
export function useEffectiveRole(): UserRole | null {
  const { user } = useAuth();
  const [state, setState] = useState<ImpersonationState | null>(() => readImpersonation());
  useEffect(() => onImpersonationChange(() => setState(readImpersonation())), []);
  // targetRole is the STORED role, so a view-only target ('viewer') maps to
  // the manager screens here, the same as when that person signs in.
  if (state) return toEffectiveRole(state.targetRole).role;
  if (user?.isPlatformOwner) return 'admin';
  return user?.role ?? null;
}

/** True when the effective user (or the act-as target) is a view-only login. */
export function useEffectiveViewOnly(): boolean {
  const { user } = useAuth();
  const [state, setState] = useState<ImpersonationState | null>(() => readImpersonation());
  useEffect(() => onImpersonationChange(() => setState(readImpersonation())), []);
  if (state) return toEffectiveRole(state.targetRole).viewOnly;
  return user?.viewOnly ?? false;
}
